import { useState } from 'react';
import { adminApi } from './api';
import { downloadXlsxTemplate } from './downloadXlsxTemplate';

const SCHEMAS = {
  modules: { title: 'Matières', columns: ['matiere','description'],
    sample: ['Matière exemple','Description pédagogique'] },
  resources: { title: 'Ressources', columns: ['module_slug','titre','description','type_ressource',
    'access_tier','url','content_text','level','duration_minutes','tags'],
    sample: ['mathematiques','Fiche exemple','','fiche','free','','Texte à compléter',
      'base','10','fondamentaux|revision'] },
  quiz_subjects: { title: 'Sujets Quiz', columns: ['module_slug','domaine_slug','titre','slug',
    'description','access_tier','display_order'],
    sample: ['mathematiques','algebre','Sujet Quiz exemple','sujet-exemple','',
      'free','1'] },
  simulation_lots: { title: 'Lots de simulations', columns: ['competition_type','titre','slug',
    'description','access_tier','display_order'],
    sample: ['direct','Lot 1','lot-1','','free','1'] },
  simulations: { title: 'Sujets de simulation', columns: ['competition_type','lot_slug','titre',
    'slug','description','duree_minutes','access_tier','display_order'],
    sample: ['direct','lot-1','Sujet 1','sujet-1','','60','free','1'] },
};

function downloadFile(name, text, type) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const element = document.createElement('a');
  element.href = url;
  element.download = name;
  document.body.appendChild(element);
  element.click();
  element.remove();
  URL.revokeObjectURL(url);
}
function downloadModel(kind) {
  const { columns, sample } = SCHEMAS[kind];
  const item = Object.fromEntries(columns.map((col, i) => [col, sample[i] || '']));
  downloadFile('modele_' + kind + '.json',
    JSON.stringify({ version: 1, items: [item] }, null, 2), 'application/json');
}

export default function BulkCatalogImport({ kind, onImported }) {
  const schema = SCHEMAS[kind];
  const [open,setOpen] = useState(false);
  const [file,setFile] = useState(null);
  const [sheets,setSheets] = useState([]);
  const [selectedSheet,setSelectedSheet] = useState('');
  const [sheetChoice,setSheetChoice] = useState('');
  const [sheetSearch, setSheetSearch] = useState('');
  const [showDialog,setShowDialog] = useState(false);
  const [preview,setPreview] = useState(null);
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState('');
  const [success,setSuccess] = useState('');
  if (!schema) return null;

  async function chooseFile(event) {
    const selected=event.target.files?.[0]||null;
    setFile(null);setSheets([]);setSelectedSheet('');setSheetChoice('');
    setSheetSearch('');
    setShowDialog(false);setPreview(null);setError('');setSuccess('');
    if(!selected) return;
    const lower=selected.name.toLowerCase();
    if(!lower.endsWith('.xlsx')&&!lower.endsWith('.json')) {
      setError('Choisissez un fichier .xlsx ou .json.');
      return;
    }
    if(selected.size>2*1024*1024) {
      setError('Taille maximale du fichier : 2 Mo.');
      return;
    }
    setBusy(true);
    try {
      if(lower.endsWith('.xlsx')) {
        const result=await adminApi.inspectWorkbookSheets(selected);
        if(!result.sheets?.length) throw new Error('Aucune feuille Excel reconnue');
        setSheets(result.sheets);
        setSheetChoice(result.sheets[0]);
        setShowDialog(true);
      } else {
        const value=typeof selected.text==='function'
          ?await selected.text()
          :await new Promise((resolve,reject)=>{
            const reader=new FileReader();
            reader.onload=()=>resolve(String(reader.result));
            reader.onerror=()=>reject(new Error('Fichier illisible'));
            reader.readAsText(selected,'utf-8');
          });
        const payload=JSON.parse(value);
        if(payload.version!==1||!Array.isArray(payload.items))
          throw new Error('JSON attendu : version 1 et tableau items.');
      }
      setFile(selected);
    } catch(err) {setError(err.message||'Fichier invalide');}
    finally {setBusy(false);}
  }

  async function run(dryRun) {
    if(!file||busy||showDialog||(sheets.length&&!selectedSheet)) return;
    setBusy(true);setError('');setSuccess('');
    try {
      const result=await adminApi.bulkImportCatalog(kind,file,dryRun,
        dryRun?'':preview?.sha256,selectedSheet);
      if(dryRun) setPreview(result);
      else {
        setSuccess(result.imported+' élément(s) ajouté(s) ; '+result.duplicate_count+
          ' doublon(s) ignoré(s).');
        setFile(null);setPreview(null);setSelectedSheet('');
        await onImported?.();
      }
    } catch(err) {setError(err.message||'Import impossible');setPreview(null);}
    finally {setBusy(false);}
  }
  return <section className="bo-panel bo-bulk-catalog" aria-label={'Importer '+schema.title}>
    <div className="bo-row bo-row--between" style={{gap:12,flexWrap:'wrap'}}>
      <div><h4>Importation massive — {schema.title}</h4>
        <p className="bo-muted">Excel ou JSON ; aperçu et confirmation obligatoires.</p></div>
      <button type="button" className="bo-button bo-button--ghost"
        aria-expanded={open} onClick={()=>setOpen(!open)}>
        {open?'Fermer':'Importer '+schema.title+' (.xlsx / .json)'}
      </button>
    </div>
    {open&&<div className="bo-stack">
      <div className="bo-actions">
        <button type="button" className="bo-button bo-button--ghost"
          onClick={()=>downloadXlsxTemplate(kind).catch(err=>setError(err.message))}>Télécharger modèle Excel (.xlsx)</button>
        <button type="button" className="bo-button bo-button--ghost"
          onClick={()=>downloadModel(kind)}>Télécharger modèle JSON</button>
      </div>
      <label>Fichier {schema.title} (.xlsx ou .json)
        <input className="bo-input" type="file"
          accept=".xlsx,.json,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={busy} onChange={chooseFile}/>
      </label>
      {selectedSheet&&<p>Feuille choisie : <strong>{selectedSheet}</strong>
        {' '}<button type="button" className="bo-button bo-button--ghost"
          onClick={()=>{setSheetChoice(selectedSheet);setPreview(null);setShowDialog(true);}}>
          Changer de feuille</button></p>}
      {showDialog&&<div role="dialog" aria-modal="true" className="bo-panel bo-sheet-choice"
        aria-labelledby={'catalog-sheet-'+kind}>
        <h4 id={'catalog-sheet-'+kind}>Sélectionner la feuille Excel</h4>
        <p className="bo-muted">Choisissez la feuille contenant les lignes à importer. Les autres feuilles sont ignorées.</p>
        <label>Filtrer les feuilles par nom
          <input className="bo-input" type="search" value={sheetSearch}
            placeholder="Rechercher un onglet…"
            onChange={(e) => setSheetSearch(e.target.value)}/>
        </label>
        <label>Feuille à utiliser
          <select className="bo-input" value={sheetChoice}
            onChange={(e)=>setSheetChoice(e.target.value)}>
            {sheets.filter(name => name.toLocaleLowerCase().includes(sheetSearch.toLocaleLowerCase())).map(x=><option key={x} value={x}>{x}</option>)}
          </select>
        </label>
        <div className="bo-actions">
          <button type="button" className="bo-button bo-button--primary"
            disabled={!sheets.filter(name => name.toLocaleLowerCase().includes(sheetSearch.toLocaleLowerCase())).includes(sheetChoice)} onClick={()=>{setSelectedSheet(sheetChoice);setPreview(null);setShowDialog(false);}}>
            Valider la feuille
          </button>
          <button type="button" className="bo-button bo-button--ghost"
            onClick={()=>setShowDialog(false)}>Annuler</button>
        </div>
      </div>}
      <div className="bo-actions">
        <button type="button" className="bo-button bo-button--secondary"
          disabled={!file||busy||showDialog||(sheets.length&&!selectedSheet)}
          onClick={()=>run(true)}>Vérifier le catalogue avant import</button>
      </div>
      {preview&&<div className="bo-panel" role="status">
        <p><strong>Aperçu :</strong> {preview.total} lignes ;
          {' '}{preview.new_count} nouvelles ; {preview.duplicate_count} doublons.</p>
        <ul>{(preview.sample||[]).map((x,i)=><li key={i}>{x}</li>)}</ul>
        <button type="button" className="bo-button bo-button--primary"
          disabled={!preview.new_count||busy} onClick={()=>run(false)}>
          Confirmer l'importation
        </button>
      </div>}
      {error&&<p role="alert" className="bo-alert bo-alert--error">{error}</p>}
      {success&&<p role="status">{success}</p>}
    </div>}
  </section>;
}
